/**
 * UploadForm.tsx — форма загрузки видеофайла (neobrutalism).
 */

import React, { useState, useRef, useCallback, DragEvent, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadVideo } from '../api/client';
import { useSSE } from '../hooks/useSSE';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Card, CardContent } from './ui/card';
import { cn } from '../lib/utils';
import type { Visibility } from '../types';

type UploadStep = 'idle' | 'uploading' | 'transcoding' | 'done' | 'error';

interface UploadFormProps {
  selectedProfiles?: string[];
}

export const UploadForm: React.FC<UploadFormProps> = ({ selectedProfiles }) => {
  const navigate = useNavigate();

  const [step, setStep] = useState<UploadStep>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [videoId, setVideoId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [visibility, setVisibility] = useState<Visibility>('public');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { status: sseStatus, profile: sseProfile, progress: sseProgress } = useSSE(
    step === 'transcoding' ? videoId : null
  );

  React.useEffect(() => {
    if (sseStatus === 'ready') {
      setStep('done');
      setTimeout(() => {
        if (videoId) navigate(`/watch/${videoId}`);
      }, 1500);
    }
    if (sseStatus === 'failed') {
      setStep('error');
      setErrorMessage('Ошибка транскодирования. Проверьте формат видео.');
    }
  }, [sseStatus, videoId, navigate]);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('video/')) {
      setErrorMessage('Разрешены только видеофайлы (mp4, avi, mkv и др.)');
      return;
    }
    setSelectedFile(file);
    setErrorMessage('');
    if (!title) setTitle(file.name.replace(/\.[^/.]+$/, ''));
  }, [title]);

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;
    setStep('uploading');
    setUploadProgress(0);
    setErrorMessage('');
    try {
      const response = await uploadVideo(
        selectedFile,
        title.trim() || selectedFile.name,
        (percent) => setUploadProgress(percent),
        selectedProfiles,
        visibility
      );
      setVideoId(response.videoId);
      setStep('transcoding');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Ошибка загрузки');
      setStep('error');
    }
  };

  function getTranscodeLabel(): string {
    if (!sseProfile) return 'Подготовка...';
    return `Транскодирование ${sseProfile}... ${sseProgress}%`;
  }

  function formatSize(bytes: number): string {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
  }

  return (
    <div className="max-w-xl mx-auto">
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
        <div className="space-y-1.5">
          <Label>Visibility</Label>
          <div className="grid grid-cols-2 gap-2">
            {(['public', 'private'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setVisibility(option)}
                className={cn(
                  'border-2 border-foreground px-3 py-2 text-sm font-black shadow-neo-sm transition-colors',
                  visibility === option ? 'bg-primary text-foreground' : 'bg-card text-foreground/70 hover:bg-muted'
                )}
              >
                {option === 'public' ? 'Public' : 'Private'}
              </button>
            ))}
          </div>
        </div>

        {/* Drag-and-drop зона */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'relative cursor-pointer border-2 border-dashed p-5 text-center transition-all duration-200 sm:p-10',
            isDragging
              ? 'border-foreground border-solid bg-primary/20 shadow-neo'
              : selectedFile
                ? 'border-foreground border-solid bg-primary/10 shadow-neo-sm'
                : 'border-foreground bg-card hover:bg-muted hover:shadow-neo-sm'
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={handleFileInputChange}
          />
          {selectedFile ? (
            <div className="space-y-2">
              <div className="text-2xl font-black text-foreground">✓</div>
              <p className="break-words text-foreground font-bold">{selectedFile.name}</p>
              <p className="text-foreground/50 text-sm font-mono">{formatSize(selectedFile.size)}</p>
              <p className="text-foreground/40 text-xs font-medium">Нажмите чтобы выбрать другой файл</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-4xl">🎥</div>
              <p className="text-foreground font-bold">Перетащите видео сюда</p>
              <p className="text-foreground/60 text-sm font-medium">или нажмите для выбора файла</p>
              <p className="text-foreground/40 text-xs font-mono">MP4, MKV, AVI, MOV — до 50 ГБ</p>
            </div>
          )}
        </div>

        {/* Поле названия */}
        <div className="space-y-1.5">
          <Label htmlFor="video-title">Название видео</Label>
          <Input
            id="video-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Введите название..."
          />
        </div>

        {/* Прогресс загрузки файла */}
        {step === 'uploading' && (
          <div className="space-y-2">
            <div className="flex flex-wrap justify-between gap-2 text-sm">
              <span className="text-foreground font-bold">Загрузка файла...</span>
              <span className="text-foreground font-mono font-black border-2 border-foreground px-1.5 bg-primary">
                {uploadProgress}%
              </span>
            </div>
            <Progress value={uploadProgress} />
          </div>
        )}

        {/* Прогресс транскодирования */}
        {step === 'transcoding' && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start gap-2">
                <div className="w-4 h-4 border-2 border-t-transparent border-foreground rounded-full animate-spin flex-shrink-0" />
                <span className="min-w-0 break-words text-sm font-mono font-bold text-foreground">{getTranscodeLabel()}</span>
              </div>
              <Progress value={sseProgress} />
              <div className="flex gap-2 text-xs font-mono font-bold flex-wrap">
                {(selectedProfiles && selectedProfiles.length > 0
                  ? selectedProfiles
                  : ['360p', '480p', '720p']
                ).map((p) => (
                  <span
                    key={p}
                    className={cn(
                      'px-1.5 py-0.5 border-2',
                      sseProfile === p
                        ? 'bg-primary border-foreground text-foreground'
                        : 'border-transparent text-foreground/40'
                    )}
                  >
                    {p}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Успех */}
        {step === 'done' && (
          <Card className="border-2 border-foreground bg-[#16A34A] shadow-neo">
            <CardContent className="p-4 text-center space-y-2">
              <div className="text-white text-2xl font-black">✓</div>
              <p className="text-white font-black text-lg">Готово!</p>
              <p className="text-white/80 text-sm font-medium">
                Видео транскодировано в {selectedProfiles?.length ?? 3} профил
                {(selectedProfiles?.length ?? 3) === 1 ? 'ь' : (selectedProfiles?.length ?? 3) <= 4 ? 'я' : 'ей'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Ошибка */}
        {(step === 'error' || errorMessage) && step !== 'done' && (
          <Card className="border-2 border-foreground bg-destructive shadow-neo">
            <CardContent className="p-4 space-y-3">
              <p className="text-destructive-foreground text-sm font-mono font-bold">{errorMessage}</p>
              <Button
                type="button"
                variant="outline"
                className="w-full bg-card text-foreground sm:w-auto"
                onClick={() => { setStep('idle'); setErrorMessage(''); }}
              >
                Попробовать снова
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Кнопка отправки */}
        {(step === 'idle' || step === 'error') && (
          <Button
            type="submit"
            disabled={!selectedFile}
            className="w-full font-black text-base"
            size="lg"
          >
            Загрузить и транскодировать
          </Button>
        )}
      </form>
    </div>
  );
};
